import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../api';
import { formatPhone } from '../phone';

type Suggestion =
  | { kind: 'person'; id: string; label: string; sub: string }
  | { kind: 'household'; id: string; label: string; sub: string };

const MAX_SUGGESTIONS = 5;

export default function NavSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const enabled = q.trim().length >= 1;
  const params: Record<string, string> = enabled ? { q: q.trim() } : {};
  const people = useSearch('people', params, { enabled });
  const households = useSearch('households', params, { enabled });

  const suggestions: Suggestion[] = enabled ? mergeSuggestions(people.data?.results, households.data?.results) : [];

  useEffect(() => {
    setHighlight(0);
  }, [q]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClickOutside);
    return () => window.removeEventListener('mousedown', onClickOutside);
  }, []);

  function selectAt(i: number) {
    const s = suggestions[i];
    if (!s) return;
    setQ('');
    setOpen(false);
    navigate(s.kind === 'person' ? `/people/${s.id}` : `/households/${s.id}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length) setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length) setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectAt(highlight);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const showDropdown = open && enabled && suggestions.length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full lg:w-64">
      <input
        type="text"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Find person or household…"
        className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-slate-500 focus:outline-none"
      />
      {showDropdown && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-80 overflow-auto rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg">
          {suggestions.map((s, i) => (
            <li
              key={`${s.kind}:${s.id}`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectAt(i);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`cursor-pointer px-2 py-1 text-sm ${
                i === highlight ? 'bg-slate-100 dark:bg-slate-800' : ''
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate">{s.label}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {s.kind}
                </span>
              </div>
              {s.sub && <div className="truncate text-xs text-slate-500 dark:text-slate-400">{s.sub}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function stripHtml(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').trim();
}

function mergeSuggestions(people: any[] | undefined, households: any[] | undefined): Suggestion[] {
  const p: Suggestion[] = (people ?? []).map((r) => ({
    kind: 'person' as const,
    id: r.id,
    label: r.name_full ?? '—',
    sub: [r.household_role, formatPhone(r.phone), r.email].filter(Boolean).join(' · '),
  }));
  const h: Suggestion[] = (households ?? []).map((r) => ({
    kind: 'household' as const,
    id: r.id,
    label: r.name_directory ?? '—',
    sub: stripHtml(r.formatted_address),
  }));

  // Interleave: prefer person, then household, repeat, capped at MAX_SUGGESTIONS.
  const out: Suggestion[] = [];
  let i = 0;
  let j = 0;
  while (out.length < MAX_SUGGESTIONS && (i < p.length || j < h.length)) {
    if (i < p.length) out.push(p[i++]);
    if (out.length >= MAX_SUGGESTIONS) break;
    if (j < h.length) out.push(h[j++]);
  }
  return out.slice(0, MAX_SUGGESTIONS);
}
